import { unwrapConnection } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiNotificationOptOutDeliveryMethodChoices,
  ApiNotificationsNotificationTypeChoices,
  NotificationDeliveryMethod,
  NotificationOptOutType,
  NotificationTypeEnums,
  useGetNotificationOptOutsQuery,
  useUpdateNotificationOptOutMutation,
  useUpdateProviderProfileMutation,
} from "@api/graphql/types-and-hooks";
import { ToggleSwitch } from "@components/ToggleSwitch/ToggleSwitch";
import { notifyError } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

type NotificationTypeDef = {
  type: ApiNotificationsNotificationTypeChoices;
  label: string;
  isVisible: (roles: string[], flags: Record<string, boolean>, isOrgPeerReviewEnabled: boolean) => boolean;
  emailSupported: boolean;
  smsSupported: boolean;
  pushSupported: boolean;
};

const PROVIDER_NOTIFICATION_TYPES: NotificationTypeDef[] = [
  {
    type: ApiNotificationsNotificationTypeChoices.ReassessmentReminder,
    label: "Reassessment reminder (45 days before due date)",
    isVisible: (roles) =>
      roles.includes("bcba") ||
      roles.includes("assessment-bcba") ||
      roles.includes("psychologist") ||
      roles.includes("evaluation-psychologist"),
    emailSupported: true,
    smsSupported: false,
    pushSupported: true,
  },
  {
    type: ApiNotificationsNotificationTypeChoices.ClinicLead,
    label: "New clinic lead",
    isVisible: (_roles, flags) => !!flags["portal-care-navigation"],
    emailSupported: true,
    smsSupported: false,
    pushSupported: true,
  },
  {
    type: ApiNotificationsNotificationTypeChoices.ExpertChatMatchAssigned,
    label: "Expert Chat: New Match Assigned",
    isVisible: (roles) => roles.includes("frontera-clarity-provider"),
    emailSupported: true,
    smsSupported: false,
    pushSupported: true,
  },
  {
    type: ApiNotificationsNotificationTypeChoices.ExpertChatNewMessage,
    label: "Expert Chat: New Message",
    isVisible: (roles) => roles.includes("frontera-clarity-provider"),
    emailSupported: true,
    smsSupported: true,
    pushSupported: true,
  },
  {
    type: ApiNotificationsNotificationTypeChoices.ExpertChatReplyNeeded,
    label: "Expert Chat: Reply Needed",
    isVisible: (roles) => roles.includes("frontera-clarity-provider"),
    emailSupported: true,
    smsSupported: false,
    pushSupported: true,
  },
  {
    type: ApiNotificationsNotificationTypeChoices.ExpertChatUnreadChats,
    label: "Expert Chat: Unread Messages",
    isVisible: (roles) => roles.includes("frontera-clarity-provider"),
    emailSupported: true,
    smsSupported: true,
    pushSupported: true,
  },
  {
    type: ApiNotificationsNotificationTypeChoices.ReportReview,
    label: "Report review updates",
    isVisible: (_roles, flags, isOrgPeerReviewEnabled) => !!flags["report-peer-review"] && isOrgPeerReviewEnabled,
    emailSupported: true,
    smsSupported: false,
    pushSupported: true,
  },
];

const DELIVERY_METHOD_MAP: Record<ApiNotificationOptOutDeliveryMethodChoices, NotificationDeliveryMethod> = {
  [ApiNotificationOptOutDeliveryMethodChoices.Email]: NotificationDeliveryMethod.Email,
  [ApiNotificationOptOutDeliveryMethodChoices.Push]: NotificationDeliveryMethod.Push,
  [ApiNotificationOptOutDeliveryMethodChoices.Sms]: NotificationDeliveryMethod.Sms,
};

export const UserManageNotificationsPage: React.FC = () => {
  const { roles, loginProviderDetailsQuery, isOrgPeerReviewEnabled } = useAdminData();
  const flags = useFlags<Record<string, boolean>>();

  const providerMetadata = loginProviderDetailsQuery.data?.getLoginProvider?.providermetadata;
  const hasPhoneNumber = !!providerMetadata?.phoneNumber;
  const [pendingSmsAgreement, setPendingSmsAgreement] = useState<boolean | null>(null);
  const smsAgreement = pendingSmsAgreement ?? providerMetadata?.smsAgreement ?? false;

  const generateSettingsLookup = () => {
    const lookup = new Map<string, boolean>();
    Object.values(ApiNotificationsNotificationTypeChoices).forEach((type) => {
      Object.values(ApiNotificationOptOutDeliveryMethodChoices).forEach((method) => {
        lookup.set(`${type}-${method}`, true);
      });
    });
    return lookup;
  };

  // 1. generate a default lookup with true values for all scenarios
  const [settingLookup, setSettingLookup] = useState<Map<string, boolean>>(() => generateSettingsLookup());

  // 2. query which notifications the user has opted out of
  const { data, isLoading, error, refetch } = useGetNotificationOptOutsQuery(
    {},
    { queryKey: ["getNotificationOptOuts"], retry: false, refetchOnWindowFocus: false },
  );

  const updateMutation = useUpdateNotificationOptOutMutation({});
  const updateProviderProfile = useUpdateProviderProfileMutation({});

  useEffect(() => {
    if (error) {
      notifyError("Failed to fetch notification settings.");
      return;
    }
    if (isLoading) return;

    const optOuts = unwrapConnection(data?.notificationOptOut) || ([] as NotificationOptOutType[]);
    const newLookup = generateSettingsLookup();
    // set to false (opted out) for any opt-outs found
    optOuts.forEach((optOut) => {
      newLookup.set(`${optOut.notificationType}-${optOut.deliveryMethod}`, false);
    });
    setSettingLookup(newLookup);
  }, [data, isLoading, error]);

  const visibleTypes = PROVIDER_NOTIFICATION_TYPES.filter((t) => t.isVisible(roles, flags, isOrgPeerReviewEnabled));

  const typesForChannel = (method: ApiNotificationOptOutDeliveryMethodChoices): NotificationTypeDef[] =>
    visibleTypes.filter((t) => {
      if (method === ApiNotificationOptOutDeliveryMethodChoices.Email) return t.emailSupported;
      if (method === ApiNotificationOptOutDeliveryMethodChoices.Sms) return t.smsSupported;
      return t.pushSupported;
    });

  const isChannelEnabled = (method: ApiNotificationOptOutDeliveryMethodChoices): boolean => {
    const applicable = typesForChannel(method);
    if (applicable.length === 0) return true;
    return applicable.some((t) => settingLookup.get(`${t.type}-${method}`) !== false);
  };

  const handleTypeToggle = async (
    type: ApiNotificationsNotificationTypeChoices,
    method: ApiNotificationOptOutDeliveryMethodChoices,
    enabled: boolean,
  ) => {
    // Implicitly enable SMS agreement when turning on an SMS sub-type directly
    if (method === ApiNotificationOptOutDeliveryMethodChoices.Sms && enabled && !smsAgreement) {
      await handleSmsToggle(true);
    }

    const key = `${type}-${method}`;
    const prevLookup = settingLookup;
    // optimistically update the state
    const newLookup = new Map(settingLookup);
    newLookup.set(key, enabled);
    setSettingLookup(newLookup);

    try {
      const response = await updateMutation.mutateAsync({
        input: {
          deliveryMethod: DELIVERY_METHOD_MAP[method],
          notificationType: type as unknown as NotificationTypeEnums,
          optedOut: !enabled, // optedOut is the inverse of enabled
        },
      });
      if (!response?.updateNotificationOptOut?.success) {
        setSettingLookup(prevLookup);
        notifyError(response?.updateNotificationOptOut?.message || "Failed to update notification settings.");
        return;
      }
      // refetch opt outs for latest state
      refetch();
    } catch {
      setSettingLookup(prevLookup);
      notifyError("Failed to update notification preferences.");
    }
  };

  const handleMasterToggle = async (method: ApiNotificationOptOutDeliveryMethodChoices, enabled: boolean) => {
    const applicable = typesForChannel(method);
    const prevLookup = settingLookup;
    // Batch all optimistic updates at once so parallel mutations don't overwrite each other
    const newLookup = new Map(settingLookup);
    applicable.forEach((t) => newLookup.set(`${t.type}-${method}`, enabled));
    setSettingLookup(newLookup);

    try {
      await Promise.all(
        applicable.map((t) =>
          updateMutation.mutateAsync({
            input: {
              deliveryMethod: DELIVERY_METHOD_MAP[method],
              notificationType: t.type as unknown as NotificationTypeEnums,
              optedOut: !enabled, // optedOut is the inverse of enabled
            },
          }),
        ),
      );
      // refetch opt outs for latest state
      refetch();
    } catch {
      setSettingLookup(prevLookup);
      notifyError("Failed to update notification preferences.");
    }
  };

  const handleSmsToggle = async (enabled: boolean) => {
    setPendingSmsAgreement(enabled);
    try {
      await updateProviderProfile.mutateAsync({ providerData: { smsAgreement: enabled } });
      await loginProviderDetailsQuery.refetch();
    } catch {
      notifyError("Failed to update SMS notification preference.");
    } finally {
      setPendingSmsAgreement(null);
    }
  };

  const handleSmsMasterToggle = async (enabled: boolean) => {
    await Promise.all([
      handleSmsToggle(enabled),
      handleMasterToggle(ApiNotificationOptOutDeliveryMethodChoices.Sms, enabled),
    ]);
  };

  const renderSubTypes = (
    types: NotificationTypeDef[],
    method: ApiNotificationOptOutDeliveryMethodChoices,
    disabled = false,
  ) => (
    <div className="ml-14 flex flex-col gap-3 mt-3">
      {types.map((t) => (
        <div key={t.type} className="flex flex-row gap-4 items-center">
          <ToggleSwitch
            enabled={settingLookup.get(`${t.type}-${method}`) !== false}
            setEnabled={(enabled) => handleTypeToggle(t.type, method, enabled)}
            disabled={disabled}
          />
          <Paragraph>{t.label}</Paragraph>
        </div>
      ))}
    </div>
  );

  const emailTypes = typesForChannel(ApiNotificationOptOutDeliveryMethodChoices.Email);
  const smsTypes = typesForChannel(ApiNotificationOptOutDeliveryMethodChoices.Sms);
  const pushTypes = typesForChannel(ApiNotificationOptOutDeliveryMethodChoices.Push);

  return (
    <div className="flex flex-col mt-10 w-full">
      <Headline size="medium">Manage Notifications</Headline>
      <Paragraph colorType="secondary" className="mt-2">
        Control which notifications you receive from Frontera
      </Paragraph>

      <div className="flex flex-col gap-8 mt-6">
        {pushTypes.length > 0 && (
          <div>
            <div className="flex flex-row gap-4 items-center">
              <ToggleSwitch
                enabled={isChannelEnabled(ApiNotificationOptOutDeliveryMethodChoices.Push)}
                setEnabled={(enabled) => handleMasterToggle(ApiNotificationOptOutDeliveryMethodChoices.Push, enabled)}
              />
              <Label displayType="normal" size="small" colorType="secondary">
                Push notifications
              </Label>
            </div>
            {renderSubTypes(pushTypes, ApiNotificationOptOutDeliveryMethodChoices.Push)}
          </div>
        )}

        {emailTypes.length > 0 && (
          <div>
            <div className="flex flex-row gap-4 items-center">
              <ToggleSwitch
                enabled={isChannelEnabled(ApiNotificationOptOutDeliveryMethodChoices.Email)}
                setEnabled={(enabled) => handleMasterToggle(ApiNotificationOptOutDeliveryMethodChoices.Email, enabled)}
              />
              <Label displayType="normal" size="small" colorType="secondary">
                Email notifications
              </Label>
            </div>
            {renderSubTypes(emailTypes, ApiNotificationOptOutDeliveryMethodChoices.Email)}
          </div>
        )}

        {smsTypes.length > 0 && (
          <div>
            <div className="flex flex-row gap-4 items-center">
              <ToggleSwitch
                enabled={
                  hasPhoneNumber && smsAgreement && isChannelEnabled(ApiNotificationOptOutDeliveryMethodChoices.Sms)
                }
                setEnabled={handleSmsMasterToggle}
                disabled={!hasPhoneNumber}
              />
              <Label displayType="normal" size="small" colorType="secondary">
                SMS notifications
              </Label>
            </div>
            {!hasPhoneNumber ? (
              <Paragraph colorType="secondary" size="small" className="mt-2 ml-14">
                Add a phone number to your{" "}
                <Link to="/settings/profile" className="text-interactive-primary-default underline">
                  profile
                </Link>{" "}
                to enable SMS notifications.
              </Paragraph>
            ) : (
              renderSubTypes(smsTypes, ApiNotificationOptOutDeliveryMethodChoices.Sms, !smsAgreement)
            )}
          </div>
        )}
      </div>
    </div>
  );
};
